import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SunIcon } from '@utils/icons';
import type { DayForecastSummary } from '@interfaces/IWeatherInsights';
import { CONDITION_ICON } from '@utils/weatherIcons';
import styles from './weatherImpactCard.module.css';

interface IForecastRibbonProps {
  days: DayForecastSummary[];
  selectedDate: string;
  onSelect: (date: string) => void;
}

export function ForecastRibbon({ days, selectedDate, onSelect }: IForecastRibbonProps) {
  const { t, i18n } = useTranslation();
  // Dates arrive from the backend already in the site/project timezone as plain
  // YYYY-MM-DD calendar days. Format the weekday in UTC so the label reflects the
  // calendar date itself, with no client-timezone assumption. The first forecast
  // day is "today".
  const locale = (i18n.language ?? 'en').startsWith('ja') ? 'ja-JP' : 'en-US';
  const { weekdayFmt, dateFmt } = useMemo(
    () => ({
      weekdayFmt: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }),
      dateFmt: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
    }),
    [locale],
  );

  return (
    <div className={styles.ribbon}>
      {days.map((d, idx) => {
        const Icon = CONDITION_ICON[d.conditionCode] ?? SunIcon;
        const selected = d.date === selectedDate;
        const asDate = new Date(`${d.date}T00:00:00Z`);
        const label = idx === 0 ? t('dashboard.weatherImpact.today') : weekdayFmt.format(asDate);
        return (
          <button
            key={d.date}
            type="button"
            aria-pressed={selected}
            className={`${styles.ribbonDay} ${selected ? (styles.ribbonDaySelected ?? '') : ''}`}
            onClick={() => onSelect(d.date)}
          >
            <span className={styles.ribbonHead}>
              <span className={styles.ribbonDayLabel}>{label}</span>
              <span className={styles.ribbonDate}>{dateFmt.format(asDate)}</span>
            </span>
            <Icon size={16} />
            {/* One-word weather (Clear/Cloudy/Rain…) — uses the short key set
                so multi-word codes like partly_cloudy don't overflow the cell. */}
            <span className={styles.ribbonCondition}>
              {t(`dashboard.weatherImpact.conditionShort.${d.conditionCode}`)}
            </span>
            <span className={styles.ribbonTemp}>
              {/* The descriptive "daytime high / overnight low" wording only needs to
                  appear once — on Today — to anchor the reading. Other days show the
                  bare high / low to keep the cards uncluttered. */}
              {idx === 0
                ? t('dashboard.weatherImpact.highLow', { hi: d.tempHiC, lo: d.tempLoC })
                : t('dashboard.weatherImpact.highLowShort', { hi: d.tempHiC, lo: d.tempLoC })}
            </span>
          </button>
        );
      })}
    </div>
  );
}
